/**
 * AxiomAllTicketsSheet — the Peptides page's "All tickets" (SCRUM-2147).
 *
 * Our own list of the account's Axiom tickets, in place of Axiom's embedded
 * widget. The widget offered "New ticket", and a ticket raised there is tied
 * to no order: nobody here is notified of Axiom's reply. A ticket is opened
 * from its order (the order's lifebuoy), so this list has no "New ticket".
 *
 * Each row links to the patient's chart and opens that ticket's thread, where
 * staff read Axiom's replies and answer them. The list follows the page's
 * facility filter, so it never offers an order the table is hiding.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { getAxiomAccountTickets, type AxiomAccountTicket } from '@/services/integrations.service';

interface AxiomAllTicketsSheetProps {
  open: boolean;
  onClose: () => void;
  /** Open the ticket's thread on this page. */
  onOpenTicket: (ticket: AxiomAccountTicket) => void;
  /** The page's facility filter: a ticket shows when its order would. */
  matchesFacility?: (facilityId: number | null) => boolean;
}

function when(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function AxiomAllTicketsSheet({ open, onClose, onOpenTicket, matchesFacility }: AxiomAllTicketsSheetProps) {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['axiom-account-tickets'],
    queryFn: getAxiomAccountTickets,
    enabled: open,
  });
  const all = data?.tickets ?? [];
  const tickets = matchesFacility ? all.filter((t) => matchesFacility(t.facilityId)) : all;

  return (
    <Sheet open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-xl flex flex-col p-0">
        <SheetHeader className="p-4 border-b border-slate-100">
          <SheetTitle className="text-sm">Axiom Support — All Tickets</SheetTitle>
        </SheetHeader>
        {data?.truncated && (
          <p className="border-b border-slate-100 bg-amber-50 px-4 py-2 text-xs text-amber-800">
            Showing the {data.limit} most recently active tickets.
          </p>
        )}
        <div className="flex-1 min-h-0 overflow-auto">
          {isLoading ? (
            <p className="p-6 text-center text-sm text-slate-500">Loading tickets…</p>
          ) : isError ? (
            <div className="p-6 text-center text-sm text-red-700" role="alert">
              Couldn&apos;t load the tickets.{' '}
              <button type="button" className="font-medium underline" onClick={() => void refetch()}>
                Try again
              </button>
            </div>
          ) : all.length === 0 ? (
            <p className="p-6 text-center text-sm text-slate-500">
              No Axiom tickets yet. Open one from the order it is about.
            </p>
          ) : tickets.length === 0 ? (
            <p className="p-6 text-center text-sm text-slate-500">
              No tickets for the selected facility.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="axiom-all-tickets">
              {tickets.map((t) => (
                <li key={t.axiomSupportTicketId} className="px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <button
                      type="button"
                      className="min-w-0 text-left text-sm font-medium text-blue-600 hover:underline"
                      onClick={() => onOpenTicket(t)}
                    >
                      {t.subject || `Ticket #${t.axiomTicketId}`}
                    </button>
                    <div className="flex shrink-0 items-center gap-2">
                      {t.unread && (
                        <span className="rounded-full bg-red-600 px-2 text-[11px] font-semibold leading-5 text-white">
                          New reply
                        </span>
                      )}
                      <Badge variant={t.status === 'resolved' ? 'success' : 'info'}>{t.status}</Badge>
                    </div>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                    {t.patientId > 0 && (
                      <Link
                        to={`/patients/${t.patientId}?section=peptides`}
                        className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                        onClick={onClose}
                      >
                        {t.patientName ?? `Patient ${t.patientId}`}
                        {t.patientDisplayId ? ` (#${t.patientDisplayId})` : ''}
                        <ExternalLink className="h-3 w-3" />
                      </Link>
                    )}
                    <button type="button" className="text-blue-600 hover:underline" onClick={() => onOpenTicket(t)}>
                      Order #{t.integrationOrderId}
                    </button>
                    {t.updatedAt && <span>Updated {when(t.updatedAt)}</span>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
